import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { Avatar, getColor, Text, Truncated } from '@frontend/design-system';
import { PROVIDER_LANE_WIDTH } from '../constants';
import { ColumnEntity } from './location-list-with-column-entities';

const TEXT_HEIGHT = 28;

interface Props extends ColumnEntity {
  shouldSetMaxWidth?: boolean;
  onClick?: () => void;
}

export const ColumnHeader = ({
  firstName,
  lastName,
  name,
  avatarVariant = 'circle',
  avatarSrc,
  iconName,
  iconColor = theme.colors.primary10,
  isArchived,
  eventCount,
  shouldSetMaxWidth = false,
  onClick,
}: Props) => {
  const { label, bgColor, icon, isUnassigned } = useMemo<{
    label: string;
    bgColor?: { fill: string; text: string };
    icon?: IconName;
    isUnassigned: boolean;
  }>(() => {
    const fullName = name || getFullName({ firstName, lastName });
    const isUnassignedColumn = fullName.toLowerCase() === 'unassigned';
    const colorIdentifier = fullName || 'default';

    return {
      label: fullName,
      bgColor: isUnassignedColumn
        ? { fill: theme.colors.neutral20, text: theme.colors.neutral70 }
        : isArchived
        ? { fill: theme.colors.neutral10, text: theme.colors.neutral60 }
        : avatarVariant === 'square'
        ? { fill: iconColor, text: theme.colors.neutral70 }
        : getColor(colorIdentifier, false),
      icon: iconName || (isUnassignedColumn ? 'help' : isArchived ? 'archive' : undefined),
      isUnassigned: isUnassignedColumn,
    };
  }, [name, firstName, lastName, isArchived, avatarVariant, iconColor, iconName]);

  const isClickable = !!onClick && !isUnassigned;

  return (
    <div css={containerStyles(shouldSetMaxWidth, isClickable)}>
      <div css={{ position: 'relative' }} onClick={onClick}>
        {avatarVariant === 'circle' ? (
          <Avatar
            size='small'
            iconName={icon}
            firstName={firstName}
            lastName={lastName}
            src={avatarSrc}
            bypassColor={bgColor}
            css={avatarBackgroundStyles(bgColor?.fill)}
            {...(isUnassigned && { title: 'Unassigned' })}
          />
        ) : (
          <div css={squareAvatarStyles(iconColor)}>
            <Icon size={24} color='default' name={iconName || 'wellness-small'} />
          </div>
        )}
        {!!eventCount && (
          <Text weight='bold' css={countStyles}>
            {eventCount}
          </Text>
        )}
      </div>
      <div css={textContainerStyles(bgColor?.fill, avatarVariant)} onClick={onClick}>
        <Truncated
          as={Text}
          size='medium'
          weight='bold'
          css={{ color: bgColor?.text }}
          tooltipOptions={{ hoverDelay: 300 }}
        >
          {label} {isArchived ? `(${'INACTIVE'})` : ''}
        </Truncated>
      </div>
    </div>
  );
};

const containerStyles = (shouldSetMaxWidth: boolean, isClickable: boolean) =>
  css({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: PROVIDER_LANE_WIDTH,
    maxWidth: shouldSetMaxWidth ? PROVIDER_LANE_WIDTH : 'unset',
    padding: theme.spacing(0, 1),
    '> div': { cursor: isClickable ? 'pointer' : 'default' },
  });

const textContainerStyles = (bgColor?: string, avatarVariant?: 'circle' | 'square') =>
  css({
    overflow: 'hidden',
    position: 'relative',
    padding: theme.spacing(0.5),
    backgroundColor: `color-mix(in srgb, ${bgColor} 60%, ${theme.colors.white})`,
    borderRadius: avatarVariant === 'circle' ? theme.borderRadius.small : 'none',
    height: TEXT_HEIGHT,
  });

const avatarBackgroundStyles = (bgColor?: string) =>
  css({
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: theme.spacing(-1),
      width: '100%',
      margin: theme.spacing(0.25, 0),
      height: TEXT_HEIGHT,
      backgroundColor: `color-mix(in srgb, ${bgColor} 60%, ${theme.colors.white})`,
      borderRadius: theme.borderRadius.small,
      zIndex: 0,
    },
  });

const squareAvatarStyles = (color: string) =>
  css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    backgroundColor: color,
    padding: theme.spacing(0.5),
  });

const countStyles = css({
  fontSize: theme.font.size.xs,
  borderRadius: theme.borderRadius.medium,
  background: theme.colors.neutral90,
  color: theme.colors.white,
  boxShadow: `${theme.shadows.innerRight}, ${theme.shadows.light}`,
  position: 'absolute',
  left: theme.spacing(-1),
  top: 0,
  minWidth: 16,
  minHeight: 16,
  textAlign: 'center',
  padding: theme.spacing(0, 0.5),
});
